import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  approvalApi,
  type ApprovalQueueItemView,
} from '../../api/endpoints.js';
import {
  ActionButton,
  Alert,
  DataTable,
  PageHeader,
  Panel,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { WorkCell } from './work-ui.js';

/**
 * THE FINAL APPROVAL QUEUE — module C8.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 5 AND 8 BEFORE ADDING ANYTHING TO THIS PAGE.
 * ===========================================================================
 *
 * APPROVAL IS A SEPARATE ACT FROM REVIEW, behind a separate permission, and it
 * records that the RECORD was checked by a second person. IT DOES NOT STATE
 * THAT THE MATERIAL PASSED (decision D104). There is no conformity wording, no
 * pass, no fail and no colour standing in for a verdict anywhere on this page.
 *
 * As on the review queue, the caller's own work is shown, flagged and refused
 * (D108) — and the server refuses it too.
 */
export function ApprovalQueuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  const [page, setPage] = useState(1);

  const queue = useQuery({
    queryKey: ['approval-queue', { page }],
    queryFn: () => approvalApi.queue({ page }),
  });

  const columns: readonly DataColumn<ApprovalQueueItemView>[] = [
    {
      key: 'work',
      header: t('work.workColumn'),
      render: (row) => <WorkCell assignment={row.assignment} to={`/work/${row.assignmentId}`} />,
    },
    {
      key: 'performedBy',
      header: t('review.performedByColumn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-700">{row.performedBy.displayName}</span>
          {row.callerIsPerformer ? (
            <StatusBadge label={t('review.ownWork')} tone="warning" />
          ) : null}
        </div>
      ),
    },
    {
      key: 'reviewed',
      header: t('approval.reviewedColumn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-700">
            {row.technicallyAcceptedBy.displayName}
          </span>
          <span className="block text-caption text-default-600">
            {formatDateTime(row.technicallyAcceptedAt, { locale }) ?? row.technicallyAcceptedAt}
          </span>
          {row.reviewNote ? (
            <span className="block truncate text-caption text-default-600" dir="auto">
              {row.reviewNote}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'revision',
      header: t('review.submittedColumn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-700">
            {formatDateTime(row.submittedAt, { locale }) ?? row.submittedAt}
          </span>
          <span className="block text-caption text-default-600">
            {t('review.revisionNumber', { number: row.revisionNumber })}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton
          priority={row.callerIsPerformer ? 'tertiary' : 'secondary'}
          size="sm"
          // The server refuses self-approval (D107); this only avoids offering
          // a control that would fail.
          isDisabled={row.callerIsPerformer}
          onPress={() => navigate(`/approval/${row.attemptId}`)}
        >
          {t('approval.openAction')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('approval.title')}
        subtitle={t('approval.subtitle')}
        breadcrumbs={[{ label: t('nav.approval') }]}
      />

      <div className="mb-gutter">
        <Alert
          tone="info"
          title={t('approval.recordCheckedTitle')}
          description={t('approval.recordCheckedBody')}
        />
      </div>

      <Panel>
        <DataTable
          label={t('approval.title')}
          columns={columns}
          rows={queue.data?.data ?? []}
          rowKey={(row) => row.attemptId}
          isLoading={queue.isLoading}
          error={queue.error}
          onRetry={() => void queue.refetch()}
          emptyContent={t('approval.none')}
          page={page}
          totalPages={queue.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
